// src/pages/admin/branches/BranchManager.jsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../../hooks/useRTL';
import {
    Box,
    Typography,
    Button,
    Paper,
    Stack,
    Chip,
    Autocomplete,
    TextField,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Skeleton
} from '@mui/material';
import {
    Add as AddIcon,
    Business as BusinessIcon,
    FilterList,
    Clear as ClearIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';

import NoPrivilege from '../../../components/common/NoPrivilege';
import NoContent from '../../../components/common/NoContent';
import SearchBar from '../../../components/common/SearchBar';
import { useBranches } from './hooks/useBranches';
import BranchTable from './components/BranchTable';
import BranchFormDialog from './components/BranchFormDialog';
import DeleteConfirmDialog from './components/DeleteConfirmDialog';

const BranchManager = () => {
    const { t } = useTranslation();
    const { isRTL } = useRTL();

    // Filter options
    const STATUS_OPTIONS = [
        { value: 'all', label: t('common.all', 'All') },
        { value: 'active', label: t('common.active', 'Active') },
        { value: 'inactive', label: t('common.inactive', 'Inactive') },
    ];

    const SORT_BY_OPTIONS = [
        { value: 'name', label: t('common.name', 'Name') },
        { value: 'location', label: t('branches.location', 'Location') },
        { value: 'createdAt', label: t('common.createdAt', 'Created At') },
    ];

    const SORT_ORDER_OPTIONS = [
        { value: 'asc', label: t('common.ascending', 'Ascending') },
        { value: 'desc', label: t('common.descending', 'Descending') },
    ];

    const {
        branches,
        loading,
        privilegeError,
        openDialog,
        selectedBranch,
        deleteConfirmOpen,
        branchToDelete,
        permissions,
        pagination,
        filters,
        hasActiveFilters,
        formik,
        getStats,
        handleOpenDialog,
        handleCloseDialog,
        handleDelete,
        handleDeleteClick,
        setDeleteConfirmOpen,
        handlePageChange,
        handleLimitChange,
        handleFilterChange,
        handleSearchChange,
        handleSortChange,
        clearFilters,
        fetchBranches,
    } = useBranches();

    const stats = getStats();

    // Permission check
    if (!permissions.canView || privilegeError) {
        return (
            <NoPrivilege
                message={t('branches.noPrivilegeError', 'You do not have permission to view branches.')}
            />
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h4" component="h1">
                        {t('branches.title', 'Branches')}
                    </Typography>
                    {!loading && (
                        <Chip
                            label={`${pagination.total} ${t('common.total', 'total')}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    )}
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchBranches}
                        disabled={loading}
                    >
                        {t('common.refresh', 'Refresh')}
                    </Button>
                    {permissions.canAdd && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                        >
                            {t('branches.addTitle', 'Add Branch')}
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="text.secondary" gutterBottom>
                                        {t('branches.totalBranches', 'Total Branches')}
                                    </Typography>
                                    <Typography variant="h4">
                                        {loading ? <Skeleton width={40} /> : stats.total}
                                    </Typography>
                                </Box>
                                <BusinessIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="text.secondary" gutterBottom>
                                        {t('branches.activeBranches', 'Active Branches')}
                                    </Typography>
                                    <Typography variant="h4">
                                        {loading ? <Skeleton width={40} /> : stats.active}
                                    </Typography>
                                </Box>
                                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="text.secondary" gutterBottom>
                                        {t('branches.inactiveBranches', 'Inactive Branches')}
                                    </Typography>
                                    <Typography variant="h4">
                                        {loading ? <Skeleton width={40} /> : stats.inactive}
                                    </Typography>
                                </Box>
                                <CancelIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters Paper */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack direction="row" alignItems="center" gap={2} mb={2}>
                    <FilterList />
                    <Typography variant="h6">{t('filters', 'Filters')}</Typography>
                    {hasActiveFilters && (
                        <Chip
                            label={t('common.filtersActive', 'Filters active')}
                            size="small"
                            color="info"
                            variant="outlined"
                        />
                    )}
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <SearchBar
                        onSearch={handleSearchChange}
                        initialValue={filters.search}
                        placeholder={t('branches.searchPlaceholder', 'Search branches...')}
                        sx={{ flexGrow: 1, minWidth: '250px' }}
                    />

                    {/* Status Filter */}
                    <Autocomplete
                        options={STATUS_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={STATUS_OPTIONS.find(opt => opt.value === filters.status)}
                        onChange={(event, newValue) => handleFilterChange('status', newValue?.value || 'all')}
                        renderInput={(params) => (
                            <TextField {...params} label={t('common.status', 'Status')} size="small" />
                        )}
                        sx={{ minWidth: 150 }}
                        disableClearable
                    />

                    {/* Sort By */}
                    <Autocomplete
                        options={SORT_BY_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={SORT_BY_OPTIONS.find(opt => opt.value === filters.sortBy)}
                        onChange={(event, newValue) => handleSortChange(newValue?.value || 'name', filters.sortOrder)}
                        renderInput={(params) => (
                            <TextField {...params} label={t('common.sortBy', 'Sort By')} size="small" />
                        )}
                        sx={{ minWidth: 150 }}
                        disableClearable
                    />

                    {/* Sort Order */}
                    <Autocomplete
                        options={SORT_ORDER_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={SORT_ORDER_OPTIONS.find(opt => opt.value === filters.sortOrder)}
                        onChange={(event, newValue) => handleSortChange(filters.sortBy, newValue?.value || 'asc')}
                        renderInput={(params) => (
                            <TextField {...params} label={t('common.order', 'Order')} size="small" />
                        )}
                        sx={{ minWidth: 150 }}
                        disableClearable
                    />

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                        <Button
                            variant="text"
                            color="inherit"
                            onClick={clearFilters}
                            startIcon={<ClearIcon />}
                            size="small"
                        >
                            {t('common.clearFilters', 'Clear')}
                        </Button>
                    )}
                </Stack>
            </Paper>

            {/* Active Filters Display */}
            {hasActiveFilters && (
                <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    {filters.search && (
                        <Chip
                            label={`${t('common.search', 'Search')}: ${filters.search}`}
                            size="small"
                            onDelete={() => handleFilterChange('search', '')}
                        />
                    )}
                    {filters.status !== 'all' && (
                        <Chip
                            label={`${t('common.status', 'Status')}: ${STATUS_OPTIONS.find(o => o.value === filters.status)?.label}`}
                            size="small"
                            color={filters.status === 'active' ? 'success' : 'default'}
                            onDelete={() => handleFilterChange('status', 'all')}
                        />
                    )}
                </Box>
            )}

            {/* Results Info */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('common.showing', 'Showing')} {pagination.total === 0 ? 0 : Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)}-{Math.min(pagination.page * pagination.limit, pagination.total)} {t('common.of', 'of')} {pagination.total} {t('common.items', 'items')}
            </Typography>

            {/* Content */}
            {loading ? (
                <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                    <CircularProgress />
                </Paper>
            ) : branches.length === 0 ? (
                <Paper sx={{ p: 4 }}>
                    <NoContent
                        icon={<BusinessIcon sx={{ fontSize: 64 }} />}
                        title={
                            hasActiveFilters
                                ? t('branches.noFilterResults', 'No branches match your filters')
                                : t('branches.noBranchesFound', 'No Branches Found')
                        }
                        description={
                            hasActiveFilters
                                ? t('branches.tryAdjustingFilters', 'Try adjusting your filter criteria')
                                : t('branches.noBranchesDescription', 'Start by adding your first branch.')
                        }
                        action={
                            permissions.canAdd && !hasActiveFilters && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleOpenDialog()}
                                >
                                    {t('branches.addTitle', 'Add Branch')}
                                </Button>
                            )
                        }
                    />
                </Paper>
            ) : (
                <BranchTable
                    branches={branches}
                    loading={loading}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleLimitChange}
                    onEdit={handleOpenDialog}
                    onDelete={handleDeleteClick}
                    canEdit={permissions.canEdit}
                    canDelete={permissions.canDelete}
                />
            )}

            {/* Dialogs */}
            <BranchFormDialog
                open={openDialog}
                onClose={handleCloseDialog}
                formik={formik}
                selectedBranch={selectedBranch}
                loading={loading}
            />

            <DeleteConfirmDialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                branch={branchToDelete}
                onConfirm={handleDelete}
                loading={loading}
            />
        </Box>
    );
};

export default BranchManager;